import { ZodError, z } from "zod";

export type PublicEnv = z.infer<typeof publicEnvSchema>;
export const publicEnvSchema = z.object({
  publicPath: z.union([z.string(), z.undefined()]),
  cdnPublicPath: z.union([z.string(), z.undefined()]),
  runtimeEnv: z.union([
    z.literal("local"),
    z.literal("test"),
    z.literal("demo"),
    z.literal("dev"),
    z.literal("prod"),
  ]),
  version: z.string(),
  faroUrl: z.string().optional(),
  dialogmoteUrl: z.string(),
  oppfolgingsplanRoot: z.string(),
});

export type ServerEnv = z.infer<typeof serverEnvSchema>;
export const serverEnvSchema = z.object({
  DINE_SYKMELDTE_BACKEND_SCOPE: z.string(),
  DINE_SYKMELDTE_BACKEND_URL: z.string(),
  FLAGGSKIPET_SCOPE: z.string(),
  FLAGGSKIPET_URL: z.string(),
  LUMI_API_HOST: z.string(),
  LUMI_API_SCOPE: z.string(),
  RUNTIME_VERSION: z.string(),
  TILTAKSPAKKEVURDERING_FEATURE_TOGGLE: z
    .union([z.literal("false"), z.literal("true")])
    .default("false"),
  // Provided my nais
  IDPORTEN_CLIENT_ID: z.string(),
  IDPORTEN_WELL_KNOWN_URL: z.string(),
  TOKEN_X_WELL_KNOWN_URL: z.string(),
  TOKEN_X_PRIVATE_JWK: z.string(),
  TOKEN_X_CLIENT_ID: z.string(),
});

/**
 * These envs are available in the browser. They are replaced during the bundling step by NextJS.
 *
 * They MUST be provided during the build step.
 */
export const browserEnv = publicEnvSchema.parse({
  publicPath: process.env.NEXT_PUBLIC_BASE_PATH,
  cdnPublicPath: process.env.NEXT_PUBLIC_ASSET_PREFIX
    ? `${process.env.NEXT_PUBLIC_ASSET_PREFIX}/public`
    : (process.env.NEXT_PUBLIC_BASE_PATH ?? ""),
  runtimeEnv: process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT,
  faroUrl: process.env.NEXT_PUBLIC_TELEMETRY_URL,
  version: process.env.NEXT_PUBLIC_VERSION,
  dialogmoteUrl: process.env.NEXT_PUBLIC_DIALOGMOTE_URL,
  oppfolgingsplanRoot: process.env.NEXT_PUBLIC_OPPFOLGINGSPLAN_ROOT,
} satisfies Record<keyof PublicEnv, string | undefined>);

const getRawServerConfig = (): Partial<unknown> =>
  ({
    // Provided by nais-*.yml
    DINE_SYKMELDTE_BACKEND_SCOPE: process.env.DINE_SYKMELDTE_BACKEND_SCOPE,
    DINE_SYKMELDTE_BACKEND_URL: process.env.DINE_SYKMELDTE_BACKEND_URL,
    FLAGGSKIPET_SCOPE: process.env.FLAGGSKIPET_SCOPE,
    FLAGGSKIPET_URL: process.env.FLAGGSKIPET_URL,
    LUMI_API_HOST: process.env.LUMI_API_HOST,
    LUMI_API_SCOPE: process.env.LUMI_API_SCOPE,
    RUNTIME_VERSION: process.env.RUNTIME_VERSION,
    TILTAKSPAKKEVURDERING_FEATURE_TOGGLE:
      process.env.TILTAKSPAKKEVURDERING_FEATURE_TOGGLE,
    // Provided by nais
    TOKEN_X_CLIENT_ID: process.env.TOKEN_X_CLIENT_ID,
    TOKEN_X_PRIVATE_JWK: process.env.TOKEN_X_PRIVATE_JWK,
    TOKEN_X_WELL_KNOWN_URL: process.env.TOKEN_X_WELL_KNOWN_URL,
    IDPORTEN_CLIENT_ID: process.env.IDPORTEN_CLIENT_ID,
    IDPORTEN_WELL_KNOWN_URL: process.env.IDPORTEN_WELL_KNOWN_URL,
  }) satisfies Record<keyof ServerEnv, string | undefined>;

type OptionalAdapterConfig = {
  url: string;
  scope: string;
};

function getOptionalAdapterConfig(
  url: string | undefined,
  scope: string | undefined,
): OptionalAdapterConfig | null {
  if (!url || !scope) {
    return null;
  }

  return {
    url,
    scope,
  };
}

export function getPaaminnelseConfig() {
  return getOptionalAdapterConfig(
    process.env.OPPFOLGINGSPLAN_BACKEND_URL,
    process.env.OPPFOLGINGSPLAN_BACKEND_SCOPE,
  );
}

export function isTiltakspakkevurderingFeatureToggleEnabled() {
  return getServerEnv().TILTAKSPAKKEVURDERING_FEATURE_TOGGLE === "true";
}

/**
 * Server envs are lazy loaded and verified using Zod.
 */
export function getServerEnv(): ServerEnv & PublicEnv {
  try {
    return {
      ...serverEnvSchema.parse(getRawServerConfig()),
      ...publicEnvSchema.parse(browserEnv),
    };
  } catch (e) {
    if (e instanceof ZodError) {
      throw new Error(
        `The following envs are missing: ${
          e.issues
            .filter(
              (it) => it.code === "invalid_type" && it.expected === "string",
            )
            .map((it) => it.path.join("."))
            .join(", ") ||
          "None are missing, but zod is not happy. Look at cause"
        }`,
        { cause: e },
      );
    } else {
      throw e;
    }
  }
}
export const isLocalOrDemo =
  process.env.NODE_ENV !== "production" || browserEnv.runtimeEnv === "demo";
